import { DialogComponent } from '@theme/dialog';

export function parseGarmentMeasurement(value) {
  const text = String(value ?? '').trim();
  if (!/^\d{1,3}(?:[.,]\d{1,2})?$/.test(text)) return null;
  const number = Number(text.replace(',', '.'));
  return number > 0 ? number : null;
}

// Broad adult-garment input guards, not body measurements or manufacturer fit limits.
function measurementLimits(fields) {
  return { width: [25, 100], shoulder: [25, 80], waist: [20, 75], inseam: [35, 110], length: fields.includes('waist') ? [60, 140] : [40, 110] };
}

export function validateGarmentMeasurements(measurements, fields) {
  const limits = measurementLimits(fields);
  const errors = {};
  for (const key of fields) {
    const range = limits[key];
    const value = measurements[key];
    if (!range) errors[key] = 'Bu ölçü karşılaştırılamıyor.';
    else if (!Number.isFinite(value) || value < range[0] || value > range[1]) errors[key] = `${range[0]}–${range[1]} cm arasında bir sayı gir.`;
  }
  if (fields.includes('inseam') && fields.includes('length') && !errors.inseam && !errors.length && measurements.inseam >= measurements.length) {
    errors.inseam = 'İç bacak, pantolonun toplam boyundan kısa olmalı. İki ölçüyü kontrol et.';
  }
  return errors;
}

/** Rank by total absolute cm difference, then smallest worst difference; disclose larger differences. */
export function compareGarment(rows, measurements, fields) {
  if (!fields.length || Object.keys(validateGarmentMeasurements(measurements, fields)).length) return { status: 'invalid' };
  if (!rows.length || rows.some((row) => Object.keys(validateGarmentMeasurements(row.measurements, fields)).length)) return { status: 'unavailable' };
  const ranked = rows.map((row) => {
    const differences = Object.fromEntries(fields.map((key) => [key, Math.round((row.measurements[key] - measurements[key]) * 100) / 100]));
    // Sum integer hundredths so decimal input cannot break a mathematical tie.
    const score = Object.values(differences).reduce((sum, value) => sum + Math.round(Math.abs(value) * 100), 0);
    return { size: row.size, differences, outsideTolerance: fields.filter((key) => Math.abs(differences[key]) > 2), score, largestDifference: Math.max(...Object.values(differences).map(Math.abs)) };
  }).sort((a, b) => a.score - b.score || a.largestDifference - b.largestDifference);
  return { status: 'match', matches: ranked.filter((row) => Math.abs(row.score - ranked[0].score) < 0.0001 && Math.abs(row.largestDifference - ranked[0].largestDifference) < 0.0001) };
}

function canonicalSize(value) {
  return String(value ?? '').trim().toUpperCase().replace(/\s*\(2XL\)$/, '').replace(/^2XL$/, 'XXL');
}

export class AlvornSizeGuideDialog extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#containFocus);
    this.addEventListener('change', this.#containMeasurementChange);
    this.addEventListener('toggle', this.#discloseOne, true);
    this.#refreshSummaries();
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.#containFocus);
    this.removeEventListener('change', this.#containMeasurementChange);
    this.removeEventListener('toggle', this.#discloseOne, true);
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refreshSummaries();
  }

  /** @param {Event | HTMLElement} [trigger] */
  showDialog(trigger) {
    // Custom elements can connect before the parser has added their children.
    this.#refreshSummaries();
    this.#syncPurchaseSizes();
    this.#showFamily(this.dataset.activeFamily ?? 'oversize');
    for (const panel of this.querySelectorAll('[data-garment-comparison]')) {
      panel.hidden = false;
      this.#measurementState(panel);
    }
    super.showDialog(trigger);
  }

  switchFamily(event) {
    const button = event.target?.closest('[data-guide-family-switch]');
    if (button) {
      this.#showFamily(button.dataset.guideFamilySwitch);
      const scroll = this.querySelector('.alvorn-size-guide-dialog__scroll');
      if (scroll) scroll.scrollTop = 0;
    }
  }

  #showFamily(family) {
    const chapters = [...this.querySelectorAll('[data-alvorn-guide-family]')];
    const active = chapters.find((chapter) => chapter.dataset.alvornGuideFamily === family) ?? chapters[0];
    if (!active) return;
    this.dataset.activeFamily = active.dataset.alvornGuideFamily;
    for (const chapter of chapters) chapter.hidden = chapter !== active;
    for (const controls of this.querySelectorAll('[data-guide-family-controls]')) controls.hidden = chapters.length < 2;
    for (const button of this.querySelectorAll('[data-guide-family-switch]')) button.setAttribute('aria-pressed', String(button.dataset.guideFamilySwitch === this.dataset.activeFamily));
  }

  #discloseOne = (event) => {
    const detail = event.target;
    if (!detail?.matches('details[open]')) return;
    const chapter = detail.closest('[data-alvorn-guide-family]');
    for (const sibling of chapter?.querySelectorAll('details[open]') ?? []) {
      if (sibling !== detail) sibling.open = false;
    }
  };

  #syncPurchaseSizes() {
    const picker = this.closest('variant-picker');
    if (!picker) return;
    const options = [...picker.querySelectorAll('[data-alvorn-option-kind="size"]')];
    for (const chapter of this.querySelectorAll('[data-alvorn-guide-family]')) {
      const family = chapter.getAttribute('data-alvorn-guide-family');
      const option = options.length === 1 ? options[0] : options.find((element) => {
        const name = element.getAttribute('data-alvorn-option-name')?.toLocaleLowerCase('tr') ?? '';
        return family === 'bottom' ? /alt|bottom/.test(name) : /üst|top/.test(name);
      });
      if (!option) continue;
      const checked = [...option.querySelectorAll('input[type="radio"]')].find((input) => input.checked);
      const size = (checked?.value ?? option.querySelector('select')?.value ?? option.getAttribute('data-option-value'))?.trim().toUpperCase();
      const button = [...chapter.querySelectorAll('[data-alvorn-guide-size]')].find((control) => canonicalSize(control.getAttribute('data-alvorn-guide-size')) === canonicalSize(size));
      if (button) this.selectSize({ target: button });
    }
  }

  clearComparison(event) {
    const panel = event.target?.closest('[data-garment-comparison]');
    if (!panel) return;
    panel.querySelector('[data-comparison-result]').replaceChildren();
    event.target.dataset.measurementTouched = 'true';
    this.#measurementState(panel);
  }

  #measurementState(panel, showAll = false) {
    const inputs = [...panel.querySelectorAll('[data-measurement]')];
    const fields = inputs.map((input) => input.dataset.measurement);
    const measurements = Object.fromEntries(inputs.map((input) => [input.dataset.measurement, parseGarmentMeasurement(input.value)]));
    const errors = validateGarmentMeasurements(measurements, fields);
    const family = panel.closest('[data-alvorn-guide-family]').dataset.alvornGuideFamily;
    for (const input of inputs) {
      if (showAll) input.dataset.measurementTouched = 'true';
      const message = (showAll || input.dataset.measurementTouched === 'true') ? errors[input.dataset.measurement] : '';
      input.setAttribute('aria-invalid', String(Boolean(message)));
      const id = `${this.querySelector('dialog').id}-${family}-${input.dataset.measurement}`;
      const hint = input.parentElement.querySelector('small');
      if (hint) hint.id = `${id}-hint`;
      let error = input.parentElement.querySelector('[data-measurement-error]');
      if (!error) {
        error = document.createElement('small');
        error.setAttribute('data-measurement-error', '');
        error.setAttribute('aria-live', 'polite');
        input.parentElement.append(error);
      }
      error.id = `${id}-error`;
      if (error.textContent !== (message || '')) error.textContent = message || '';
      input.setAttribute('aria-describedby', [hint?.id, error.id].filter(Boolean).join(' '));
    }
    return { inputs, fields, measurements, errors };
  }

  // The enclosing native variant-picker treats every bubbling change as a product option.
  #containMeasurementChange = (event) => {
    if (event.target?.matches('[data-measurement]')) event.stopPropagation();
  };

  compareMeasurements(event) {
    const panel = event.target?.closest('[data-garment-comparison]');
    const chapter = panel?.closest('[data-alvorn-guide-family]');
    if (!panel || !chapter) return;
    const output = panel.querySelector('[data-comparison-result]');
    const { inputs, fields, measurements, errors } = this.#measurementState(panel, true);
    const invalid = inputs.filter((input) => errors[input.dataset.measurement]);
    if (invalid.length) {
      output.textContent = 'İşaretli ölçüleri kontrol edip tekrar karşılaştır.';
      invalid[0].focus();
      return;
    }
    const headers = [...chapter.querySelectorAll('thead [data-alvorn-guide-column]')];
    const rows = headers.map((header) => ({
      size: header.getAttribute('data-alvorn-guide-column'),
      measurements: Object.fromEntries(fields.map((key) => {
        const row = chapter.querySelector(`[data-comparison-row="${key}"]`)?.closest("tr");
        const cell = [...(row?.querySelectorAll('[data-alvorn-guide-column]') ?? [])].find((cell) => cell.getAttribute('data-alvorn-guide-column') === header.getAttribute('data-alvorn-guide-column'));
        return [key, parseGarmentMeasurement(cell?.textContent?.replace(/\s*cm\s*$/, ''))];
      })),
    }));
    const result = compareGarment(rows, measurements, fields);
    output.replaceChildren();
    if (result.status !== 'match') {
      output.textContent = 'Ölçüler şu anda karşılaştırılamıyor. Aşağıdaki tabloyu kullanabilirsin.';
      return;
    }
    const heading = document.createElement('p');
    heading.textContent = result.matches.length > 1 ? 'Parçana en yakın iki seçenek:' : 'Parçana en yakın beden:';
    if (result.matches.length > 2) heading.textContent = 'Parçana eşit yakınlıktaki bedenler:';
    output.append(heading);
    for (const match of result.matches) {
      const item = document.createElement('div');
      const name = document.createElement('strong');
      name.textContent = match.size;
      const differences = document.createElement('p');
      differences.textContent = inputs.map((input) => {
        const delta = match.differences[input.dataset.measurement];
        const value = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(Math.abs(delta));
        return `${input.dataset.label}: ${delta === 0 ? 'aynı' : `${value} cm ${delta > 0 ? 'daha fazla' : 'daha az'}`}`;
      }).join(' · ');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = `${match.size} ölçülerini göster`;
      button.addEventListener('click', () => {
        const control = [...chapter.querySelectorAll('[data-alvorn-guide-size]')].find((control) => control.getAttribute('data-alvorn-guide-size') === match.size);
        if (control) {
          panel.open = false;
          this.selectSize({ target: control });
          control.focus();
        }
      });
      item.append(name, differences);
      if (match.outsideTolerance.length) {
        const notice = document.createElement('p');
        notice.className = 'alvorn-garment-comparison__difference-note';
        const labels = inputs.filter((input) => match.outsideTolerance.includes(input.dataset.measurement)).map((input) => input.dataset.label.toLocaleLowerCase('tr'));
        notice.textContent = `Kontrol et: ${labels.join(', ')} ${labels.length > 1 ? 'ölçülerinde' : 'ölçüsünde'} 2 cm’den fazla fark var.`;
        item.append(notice);
      }
      item.append(button);
      output.append(item);
    }
    const note = document.createElement('p');
    note.textContent = 'Parçanın ölçülerine göre karşılaştırıldı. Satın almak için ürün sayfasında bu bedeni seç.';
    output.append(note);
    output.scrollIntoView?.({ block: 'start' });
  }

  /**
   * Changes only the displayed measurement column; never writes purchase options.
   * @param {Event | { target?: EventTarget | null, currentTarget?: EventTarget | null }} event
   */
  selectSize(event) {
    const eventTarget = event?.target;
    const currentTarget = event?.currentTarget;
    const button = eventTarget?.matches?.('[data-alvorn-guide-size]')
      ? eventTarget
      : currentTarget?.matches?.('[data-alvorn-guide-size]')
        ? currentTarget
        : null;
    if (!button) return;

    const chapter = button.closest('[data-alvorn-guide-family]');
    const size = button.getAttribute('data-alvorn-guide-size');
    if (!chapter || !size) return;

    for (const control of chapter.querySelectorAll('[data-alvorn-guide-size]')) {
      control.setAttribute('aria-pressed', String(control === button));
    }
    chapter.setAttribute('data-selected-size', size);
    this.#renderSummary(chapter, size);
  }

  #containFocus = (event) => {
    if (event.key === 'Enter' && event.target?.matches('[data-measurement]')) {
      event.preventDefault();
      this.compareMeasurements(event);
      return;
    }
    const dialog = this.refs.dialog;
    if (event.key !== 'Tab' || !dialog?.open) return;

    const focusable = [...dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"]), .alvorn-size-guide-dialog__table-scroll'
    )].filter((element) => element.getClientRects().length > 0);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #refreshSummaries() {
    for (const chapter of this.querySelectorAll('[data-alvorn-guide-family]')) {
      const selected = chapter.querySelector('[data-alvorn-guide-size][aria-pressed="true"]')
        ?? chapter.querySelector('[data-alvorn-guide-size]');
      const size = selected?.getAttribute('data-alvorn-guide-size');
      if (size) {
        chapter.setAttribute('data-selected-size', size);
        this.#renderSummary(chapter, size);
      }
    }
  }

  /** @param {Element} chapter @param {string} size */
  #renderSummary(chapter, size) {
    const summary = chapter.querySelector('[data-alvorn-guide-summary]');
    const table = chapter.querySelector('table');
    if (!(summary instanceof HTMLElement) || !table) return;

    for (const cell of table.querySelectorAll('[data-alvorn-guide-column]')) {
      cell.classList.toggle('is-selected', cell.getAttribute('data-alvorn-guide-column') === size);
    }

    const headerCells = [...table.querySelectorAll('thead th')];
    const columnIndex = headerCells.findIndex((cell) => cell.getAttribute('data-alvorn-guide-column') === size);
    if (columnIndex < 1) return;

    const list = document.createElement('dl');
    const heading = document.createElement('p');
    heading.className = 'alvorn-size-guide-dialog__summary-size';
    heading.textContent = `${size} ölçüleri`;

    for (const row of table.querySelectorAll('tbody tr')) {
      const cells = row.querySelectorAll('th, td');
      const label = cells[0];
      const value = cells[columnIndex];
      if (!label || !value) continue;
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label.firstChild?.textContent?.trim() ?? label.textContent?.trim() ?? '';
      description.textContent = value.textContent?.trim() ?? '';
      list.append(term, description);
    }

    summary.replaceChildren(heading, list);
  }
}

if (!customElements.get('alvorn-size-guide-dialog')) {
  customElements.define('alvorn-size-guide-dialog', AlvornSizeGuideDialog);
}
